import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import CodeSegment from "@/components/CodeSegment";
import ObservationDialog, { photoSrc } from "@/components/ObservationDialog";
import PdfActions from "@/components/PdfActions";
import ItemPhotos from "@/components/ItemPhotos";
import SignaturesSection from "@/components/SignaturesSection";
import { RegblButton } from "@/components/RegblButton";
import { useAuth } from "@/context/AuthContext";
import { cacheEtat, readCachedEtat, enqueueSave, hasPending, flushQueue, isNetworkError, onQueueChange } from "@/lib/offline";
import { locataires } from "@/lib/parties";
import {
  ArrowLeft, ArrowRight, CheckCheck, Plus, Trash2, Pencil, Check, Info, Camera, FileWarning, WifiOff, Mail,
  ClipboardList, ListChecks, Save, Lock, CheckCircle2, Undo2, Redo2, Tag, RefreshCw,
} from "lucide-react";

const TYPE_LABEL = { entree: "Entrée", sortie: "Sortie", entree_sortie: "Entrée + sortie" };
const IMPUT_LABEL = { locataire: "Locataire", bailleur: "Bailleur", usure_normale: "Usure normale" };

const PIECE_TYPES = [
  ["entree", "Entrée"], ["sejour", "Séjour"], ["chambre", "Chambre"], ["cuisine", "Cuisine"],
  ["salle_de_bains", "Salle de bains"], ["wc_separe", "WC séparé"], ["balcon", "Balcon"],
  ["cave", "Cave"], ["galetas", "Galetas"], ["buanderie", "Buanderie"],
  ["place_parc", "Place de parc"], ["local_commercial", "Local"], ["general", "Général"],
];

export default function ConstatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [etat, setEtat] = useState(null);
  const [step, setStep] = useState(0); // 0=infos, 1..n=pieces, n+1=synthese
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const history = useRef([]);
  const future = useRef([]);
  const [histLen, setHistLen] = useState({ back: 0, fwd: 0 });
  const saveTimer = useRef(null);
  const [obsDialog, setObsDialog] = useState({ open: false, obs: null });
  const [addPieceOpen, setAddPieceOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [ligneEdit, setLigneEdit] = useState(null); // {pieceId, ligneId|null, value}
  const [appareilEdit, setAppareilEdit] = useState(null); // {pieceId, ligneId, libelle, value:{marque,modele,numero_serie,photos}}
  const saveAppareil = () => {
    const { pieceId, ligneId, value } = appareilEdit;
    update((prev) => ({ ...prev, pieces: prev.pieces.map((pc) => pc.id !== pieceId ? pc : { ...pc, lignes: pc.lignes.map((l) => l.id === ligneId ? { ...l, appareil: value } : l) }) }));
    setAppareilEdit(null);
  };
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    if (!ligneEdit || ligneEdit.ligneId) return;
    const pc = (etat?.pieces || []).find((p) => p.id === ligneEdit.pieceId);
    api.get("/catalogue/elements/suggestions", { params: { type_piece: pc?.type_piece } }).then((r) => setSuggestions(r.data)).catch(() => setSuggestions([]));
    // eslint-disable-next-line
  }, [ligneEdit?.pieceId, ligneEdit?.ligneId]);
  const [confirm, setConfirm] = useState(null); // {title, message, onConfirm}
  const [reopen, setReopen] = useState(null); // motif string when dialog open
  const [pendingSync, setPendingSync] = useState(hasPending(id));
  const [offlineData, setOfflineData] = useState(false);
  useEffect(() => onQueueChange(() => { if (!hasPending(id)) { setPendingSync(false); setOfflineData(false); setSavedAt(new Date()); } }), [id]);
  useEffect(() => {
    const h = (e) => { if (e.detail?.id === id) setEtat((prev) => (prev ? { ...prev, observations: e.detail.observations } : prev)); };
    window.addEventListener("madlo:etat-updated", h);
    return () => window.removeEventListener("madlo:etat-updated", h);
  }, [id]);
  const { user } = useAuth();

  const reprendreSortie = async () => {
    try {
      const { data } = await api.post(`/etats/${id}/reprendre-sortie`);
      toast.success(data.existant ? "Constat de sortie déjà créé — ouverture" : "Constat de sortie créé à partir de l'entrée");
      navigate(`/constats/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const marquerRelu = async () => {
    try {
      const { data } = await api.post(`/etats/${id}/import-relu`);
      setEtat(data);
      toast.success("Constat marqué comme relu et validé");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doReopen = async () => {
    try {
      const { data } = await api.post(`/etats/${id}/reouvrir`, { motif: reopen });
      setEtat(data); setReopen(null);
      toast.success("Constat réouvert — les signatures ont été archivées dans le journal");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const locked = etat && (etat.statut === "signe" || etat.statut === "cloture");

  useEffect(() => {
    api.get(`/etats/${id}`).then((r) => {
      const cached = readCachedEtat(id);
      const pendingLocal = hasPending(id) && cached?.etat;
      setEtat(pendingLocal ? cached.etat : r.data);
      if (!pendingLocal) cacheEtat(r.data);
      if (pendingLocal) flushQueue();
    }).catch((err) => {
      const cached = readCachedEtat(id);
      if (isNetworkError(err) && cached?.etat) {
        setEtat(cached.etat); setOfflineData(true);
        toast.message("Mode hors ligne : constat chargé depuis la tablette");
      } else { toast.error("Constat introuvable"); navigate("/constats"); }
    });
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line
  }, [id]);

  const persist = useCallback(async (data) => {
    setSaving(true);
    cacheEtat(data);
    try {
      await api.put(`/etats/${id}`, data);
      setSavedAt(new Date()); setPendingSync(false); setDirty(false);
    } catch (err) {
      if (isNetworkError(err) || err?.response?.status === 401) {
        enqueueSave(id, data); setPendingSync(true); setDirty(false);
        if (err?.response?.status === 401) toast.error("Session expirée — vos modifications sont conservées sur la tablette et seront synchronisées après reconnexion");
      } else toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id]);

  const saveNow = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    setEtat((prev) => { persist(prev); return prev; });
  };

  // met à jour l'état local + planifie une sauvegarde (debounce) + historique annuler/rétablir
  const update = (updater) => {
    setEtat((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      history.current = [...history.current.slice(-49), prev];
      future.current = [];
      setHistLen({ back: history.current.length, fwd: 0 });
      setDirty(true);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(next), 700);
      return next;
    });
  };

  const travel = (dir) => {
    setEtat((prev) => {
      const from = dir < 0 ? history.current : future.current;
      if (!from.length) return prev;
      const target = from.pop();
      (dir < 0 ? future : history).current.push(prev);
      setHistLen({ back: history.current.length, fwd: future.current.length });
      setDirty(true);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(target), 700);
      return target;
    });
  };

  useEffect(() => {
    const h = (e) => {
      if (!(e.ctrlKey || e.metaKey) || locked) return;
      const k = e.key.toLowerCase();
      if (k === "s") { e.preventDefault(); saveNow(); return; }
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.isContentEditable;
      if (inField) return;
      if (k === "z" && !e.shiftKey) { e.preventDefault(); travel(-1); }
      else if (k === "y" || (k === "z" && e.shiftKey)) { e.preventDefault(); travel(1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line
  }, [locked]);

  if (!etat) return <div className="text-dd-blue">Chargement…</div>;

  const pieces = etat.pieces || [];
  const steps = ["infos", ...pieces.map((p) => p.id), "synthese"];
  const isPiece = step >= 1 && step <= pieces.length;
  const currentPiece = isPiece ? pieces[step - 1] : null;

  const setField = (k, v) => update((p) => ({ ...p, [k]: v }));

  // ---- lignes ----
  const nextObsNumero = () => {
    const nums = (etat.observations || []).map((o) => o.numero);
    return nums.length ? Math.max(...nums) + 1 : 1;
  };

  const makeObs = (observations, pc, l, nature) => {
    const numero = observations.length ? Math.max(...observations.map((o) => o.numero)) + 1 : 1;
    return {
      numero, piece: pc.libelle, element: l.element_libelle, nature,
      description: "", imputation: nature === "usure_normale" ? "usure_normale" : "", photos: [],
      montant: null, annee_refection: null, duree_amortissement: null,
      part_locataire: null, part_bailleur: null, ligne_id: l.id,
    };
  };

  const setLigneCode = (pieceId, ligneId, col, code) => {
    update((prev) => {
      const observations = [...(prev.observations || [])];
      const newPieces = prev.pieces.map((pc) => {
        if (pc.id !== pieceId) return pc;
        const lignes = pc.lignes.map((l) => {
          if (l.id !== ligneId) return l;
          const updated = { ...l, [col]: code };
          if (code === "D" && !l.numero_observation) {
            const obs = makeObs(observations, pc, l, "defaut");
            observations.push(obs);
            updated.numero_observation = obs.numero;
            setTimeout(() => setObsDialog({ open: true, obs }), 50);
          } else if (code === "D" && l.numero_observation) {
            const idx = observations.findIndex((o) => o.numero === l.numero_observation);
            if (idx >= 0 && observations[idx].nature !== "defaut") observations[idx] = { ...observations[idx], nature: "defaut", imputation: observations[idx].imputation === "usure_normale" ? "" : observations[idx].imputation };
          }
          return updated;
        });
        return { ...pc, lignes };
      });
      return { ...prev, pieces: newPieces, observations };
    });
  };

  // observation facultative (usure normale, remarque, photo)
  const addNoteObs = (pieceId, ligneId) => {
    update((prev) => {
      const observations = [...(prev.observations || [])];
      const newPieces = prev.pieces.map((pc) => {
        if (pc.id !== pieceId) return pc;
        return { ...pc, lignes: pc.lignes.map((l) => {
          if (l.id !== ligneId || l.numero_observation) return l;
          const obs = makeObs(observations, pc, l, "usure_normale");
          observations.push(obs);
          setTimeout(() => setObsDialog({ open: true, obs }), 50);
          return { ...l, numero_observation: obs.numero };
        }) };
      });
      return { ...prev, pieces: newPieces, observations };
    });
  };

  const toutEnOrdre = (pieceId) => {
    update((prev) => ({
      ...prev,
      pieces: prev.pieces.map((pc) => {
        if (pc.id !== pieceId) return pc;
        return {
          ...pc,
          lignes: pc.lignes.map((l) => {
            const nl = { ...l };
            if (etat.type_constat === "entree" || etat.type_constat === "entree_sortie") nl.code_entree = "EO";
            if (etat.type_constat === "sortie" || etat.type_constat === "entree_sortie") nl.code_sortie = "EO";
            return nl;
          }),
        };
      }),
    }));
    toast.success("Pièce remplie en « en ordre »");
  };

  // ---- observations ----
  const saveObs = () => {
    const o = obsDialog.obs;
    if (o.nature === "defaut" && !(o.description || "").trim()) {
      toast.error("Le commentaire est obligatoire pour un défaut");
      return;
    }
    update((prev) => ({
      ...prev,
      observations: prev.observations.map((x) => (x.numero === o.numero ? o : x)),
    }));
    setObsDialog({ open: false, obs: null });
  };

  const editObs = (obs) => setObsDialog({ open: true, obs: { ...obs } });

  const deleteObs = (numero) => {
    update((prev) => ({
      ...prev,
      observations: prev.observations.filter((o) => o.numero !== numero),
      pieces: prev.pieces.map((pc) => ({
        ...pc,
        lignes: pc.lignes.map((l) => (l.numero_observation === numero ? { ...l, numero_observation: null } : l)),
      })),
    }));
  };

  // ---- pièces ----
  const addPiece = (typeCode, label) => {
    api.get("/catalogue/elements", { params: { type_piece: typeCode } }).then((r) => {
      const lignes = r.data.map((e) => ({
        id: crypto.randomUUID(), element_libelle: e.libelle,
        code_entree: "", code_sortie: "", numero_observation: null,
      }));
      update((prev) => ({
        ...prev,
        pieces: [...prev.pieces, {
          id: crypto.randomUUID(), type_piece: typeCode, libelle: label,
          ordre: prev.pieces.length + 1, lignes,
        }],
      }));
      setAddPieceOpen(false);
      toast.success("Pièce ajoutée");
    });
  };

  const removePiece = (pieceId) => {
    update((prev) => {
      const piece = prev.pieces.find((p) => p.id === pieceId);
      const nums = new Set((piece?.lignes || []).map((l) => l.numero_observation).filter(Boolean));
      return { ...prev, pieces: prev.pieces.filter((p) => p.id !== pieceId),
        observations: (prev.observations || []).filter((o) => !nums.has(o.numero)) };
    });
    setStep(0);
    toast.success("Pièce supprimée");
  };

  const askRemovePiece = (p) => setConfirm({
    title: "Supprimer cette pièce ?",
    message: `« ${p.libelle} » et ses ${p.lignes.length} éléments seront supprimés du constat.`,
    onConfirm: () => removePiece(p.id),
  });

  const renamePiece = (pieceId, libelle) => {
    update((prev) => ({
      ...prev,
      pieces: prev.pieces.map((p) => (p.id === pieceId ? { ...p, libelle } : p)),
    }));
  };

  // ---- éléments (lignes) ----
  const saveLigne = () => {
    const { pieceId, ligneId, value } = ligneEdit;
    const libelle = value.trim();
    if (!libelle) return toast.error("Le libellé est requis");
    update((prev) => ({
      ...prev,
      pieces: prev.pieces.map((pc) => {
        if (pc.id !== pieceId) return pc;
        if (ligneId) return { ...pc, lignes: pc.lignes.map((l) => (l.id === ligneId ? { ...l, element_libelle: libelle } : l)) };
        return { ...pc, lignes: [...pc.lignes, { id: crypto.randomUUID(), element_libelle: libelle, code_entree: "", code_sortie: "", numero_observation: null }] };
      }),
      observations: ligneId
        ? (prev.observations || []).map((o) => (o.ligne_id === ligneId ? { ...o, element: libelle } : o))
        : prev.observations,
    }));
    setLigneEdit(null);
    toast.success(ligneId ? "Élément renommé" : "Élément ajouté");
  };

  const removeLigne = (pieceId, ligne) => {
    update((prev) => ({
      ...prev,
      pieces: prev.pieces.map((pc) => (pc.id === pieceId ? { ...pc, lignes: pc.lignes.filter((l) => l.id !== ligne.id) } : pc)),
      observations: (prev.observations || []).filter((o) => o.numero !== ligne.numero_observation),
    }));
    toast.success("Élément supprimé");
  };

  // ---- clés / compteurs / conditions ----
  const addCle = () => update((p) => ({ ...p, cles: [...(p.cles || []), { designation: "", numero: "", nombre: 1, date_remise: p.type_constat !== "sortie" ? (p.date_constat || "") : "", date_restitution: p.type_constat === "sortie" ? (p.date_constat || "") : "" }] }));
  const addCompteur = () => update((p) => ({ ...p, compteurs: [...(p.compteurs || []), { type: "", numero: "", index_entree: "", index_sortie: "" }] }));

  return (
    <div>
      {/* Header sticky */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <button onClick={() => navigate("/constats")} className="flex items-center gap-2 text-dd-blue text-sm">
          <ArrowLeft size={16} /> Constats
        </button>
        <div className="flex items-center gap-3 text-sm">
          <PdfActions etat={etat} compact onSent={(envois) => setEtat((p) => ({ ...p, envois: [...(p.envois || []), ...envois] }))} />
          {(locked || etat.import_source) && etat.type_constat !== "sortie" && (
            <Button size="sm" variant="outline" onClick={reprendreSortie} data-testid="reprendre-sortie-btn" className="h-9 border-dd-blue text-dd-blue">
              <ArrowRight size={14} className="mr-1" /> {etat.sortie_id ? "Ouvrir le constat de sortie" : "Créer le constat de sortie"}
            </Button>
          )}
          {locked && user?.role === "admin" && (
            <Button size="sm" variant="outline" onClick={() => setReopen("")} data-testid="reopen-btn" className="h-9 border-dd-light text-dd-night">
              <Lock size={14} className="mr-1" /> Réouvrir
            </Button>
          )}
          {locked && <Badge className="bg-dd-night text-white border-0"><Lock size={12} className="mr-1" /> Signé — verrouillé</Badge>}
          {!locked && (
            <div className="flex items-center gap-1 border border-dd-light rounded-md bg-white px-1" data-testid="edit-toolbar">
              <button onClick={() => travel(-1)} disabled={!histLen.back} title="Annuler (Ctrl+Z)" data-testid="undo-btn"
                className="h-9 w-9 inline-flex items-center justify-center rounded text-dd-blue disabled:text-dd-light disabled:cursor-not-allowed hover:bg-dd-light/60 transition-colors">
                <Undo2 size={16} />
              </button>
              <button onClick={() => travel(1)} disabled={!histLen.fwd} title="Rétablir (Ctrl+Y)" data-testid="redo-btn"
                className="h-9 w-9 inline-flex items-center justify-center rounded text-dd-blue disabled:text-dd-light disabled:cursor-not-allowed hover:bg-dd-light/60 transition-colors">
                <Redo2 size={16} />
              </button>
              <span className="w-px h-6 bg-dd-light mx-0.5" />
              <button onClick={saveNow} disabled={!dirty || saving} title={dirty ? "Enregistrer maintenant (Ctrl+S)" : "Tout est enregistré"} data-testid="save-btn"
                data-dirty={dirty ? "1" : "0"}
                className={`h-9 w-9 inline-flex items-center justify-center rounded transition-colors ${dirty ? "text-dd-blue hover:bg-dd-light/60" : "text-dd-light cursor-default"}`}>
                <Save size={16} className={saving ? "animate-pulse" : ""} />
              </button>
            </div>
          )}
          <span className="text-dd-warm flex items-center gap-1.5" data-testid="save-indicator">
            {saving ? <><Save size={14} className="animate-pulse" /> Enregistrement…</>
              : dirty ? <span className="text-dd-blue">Modifications en attente</span>
              : pendingSync ? <><WifiOff size={14} className="text-destructive" /> Enregistré sur la tablette — en attente de réseau</>
              : offlineData ? <><WifiOff size={14} /> Données locales (hors ligne)</>
              : savedAt ? <><Check size={14} className="text-dd-blue" /> Enregistré à {savedAt.toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}</>
              : "Modifications enregistrées automatiquement"}
          </span>
        </div>
      </div>

      <div className="flex items-start gap-2 mb-2">
        <h1 className="text-2xl font-bold text-dd-blue">
          {etat.numero_dossier ? `Dossier ${etat.numero_dossier} · ` : ""}{etat.immeuble || "Constat"}
        </h1>
      </div>
      <p className="text-sm text-dd-warm mb-6">
        {TYPE_LABEL[etat.type_constat]} · {etat.typologie_libelle || "—"}
      </p>
      {etat.source_entree_id && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-lg border border-dd-blue/30 bg-dd-blue/5 px-4 py-3 text-sm text-dd-night" data-testid="reprise-banner">
          <Info size={16} className="text-dd-blue shrink-0" />
          <span>Repris du constat d'entrée{etat.source_entree_date ? ` du ${new Date(etat.source_entree_date).toLocaleDateString("fr-CH")}` : ""} : la colonne « Entrée » est reprise telle que signée (lecture seule), seule la colonne « Sortie » est à remplir.</span>
          <button onClick={() => navigate(`/constats/${etat.source_entree_id}`)} className="text-dd-blue underline" data-testid="open-entree-link">Voir l'entrée</button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Steps nav */}
        <aside className="lg:w-64 shrink-0">
          <div className="bg-white rounded-lg border border-dd-light p-2 lg:sticky lg:top-4">
            <StepBtn active={step === 0} onClick={() => setStep(0)} icon={Info} label="Informations générales" testid="step-infos" />
            <div className="mt-2 mb-1 px-3 text-xs uppercase tracking-wider text-dd-warm">Pièces</div>
            {pieces.map((p, i) => {
              const done = p.lignes.every((l) => (etat.type_constat === "sortie" ? l.code_sortie : l.code_entree));
              return (
                <div key={p.id} className="group flex items-center">
                  <StepBtn active={step === i + 1} onClick={() => setStep(i + 1)}
                    icon={ClipboardList} label={p.libelle} testid={`step-piece-${i}`} done={done} />
                  {!locked && (
                    <div className="flex shrink-0">
                      <button onClick={() => setRenaming({ id: p.id, value: p.libelle })} title="Modifier la pièce"
                        className="h-10 w-8 flex items-center justify-center text-dd-blue/70 hover:text-dd-blue" data-testid={`nav-edit-piece-${i}`}><Pencil size={14} /></button>
                      <button onClick={() => askRemovePiece(p)} title="Supprimer la pièce"
                        className="h-10 w-8 flex items-center justify-center text-destructive/70 hover:text-destructive" data-testid={`nav-delete-piece-${i}`}><Trash2 size={14} /></button>
                    </div>
                  )}
                </div>
              );
            })}
            {!locked && (
              <button onClick={() => setAddPieceOpen(true)} data-testid="nav-add-piece-btn"
                className="w-full h-11 mt-1 flex items-center gap-2.5 px-3 rounded-md text-sm text-dd-blue border border-dashed border-dd-blue/40 hover:bg-dd-light/60">
                <Plus size={16} /> Ajouter une pièce
              </button>
            )}
            <StepBtn active={step === pieces.length + 1} onClick={() => setStep(pieces.length + 1)}
              icon={ListChecks} label="Synthèse & suites" testid="step-synthese" />
          </div>
        </aside>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {step === 0 && <InfosStep etat={etat} setField={setField} update={update} locked={locked} addCle={addCle} addCompteur={addCompteur} marquerRelu={marquerRelu} />}

          {isPiece && currentPiece && (
            <div className="bg-white rounded-lg border border-dd-light overflow-hidden" data-testid="piece-panel">
              {/* Bandeau bleu */}
              <div className="bg-dd-blue text-white px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold" data-testid="piece-title">{currentPiece.libelle}</div>
                  <div className="text-sm text-dd-light" data-testid="piece-progress">
                    {currentPiece.libelle}, {step} sur {pieces.length}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!locked && (
                    <>
                      <Button size="sm" variant="secondary" data-testid="rename-piece-btn"
                        onClick={() => setRenaming({ id: currentPiece.id, value: currentPiece.libelle })}
                        className="bg-white/15 hover:bg-white/25 text-white border-0">
                        <Pencil size={14} className="mr-1" /> Renommer
                      </Button>
                      <Button size="sm" data-testid="tout-en-ordre-btn" onClick={() => toutEnOrdre(currentPiece.id)}
                        className="bg-white text-dd-blue hover:bg-dd-light">
                        <CheckCheck size={16} className="mr-1" /> Tout en ordre
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Légende des codes */}
              <div className="flex flex-wrap gap-x-5 gap-y-1 px-4 py-2 text-xs text-dd-night bg-white border-b border-dd-light" data-testid="code-legend">
                {[["EO", "En ordre"], ["UN", "Usure normale (remarque/photo facultative via l'icône appareil photo)"], ["D", "Défaut → observation avec commentaire obligatoire, photo recommandée"], ["SO", "Sans objet"]].map(([c, l]) => (
                  <span key={c} className="flex items-center gap-1.5">
                    <span className={`inline-flex items-center justify-center min-w-[30px] h-6 px-1.5 rounded text-[11px] font-semibold ${c === "D" ? "bg-destructive text-white" : "bg-dd-blue text-white"}`}>{c}</span>
                    {l}
                  </span>
                ))}
              </div>

              {/* Lignes */}
              <div className="divide-y divide-dd-light">
                {(() => {
                  const showEntree = etat.type_constat !== "sortie" || !!etat.source_entree_id;
                  const showSortie = etat.type_constat !== "entree";
                  const colW = (showEntree && showSortie && !etat.source_entree_id) ? "w-[196px] shrink-0" : "w-[220px] sm:w-[236px] shrink-0";
                  return (
                    <>
                      <div className="hidden sm:flex gap-3 px-4 py-2 bg-dd-light/40 text-xs font-semibold text-dd-night uppercase tracking-wide">
                        <div className="flex-1 min-w-0">Élément</div>
                        {showEntree && <div className={etat.source_entree_id ? "w-[64px] shrink-0 text-center" : colW}>Entrée</div>}
                        {showSortie && <div className={colW}>Sortie</div>}
                        {!locked && <div className="w-[128px] shrink-0" />}
                      </div>
                      {currentPiece.lignes.map((l) => (
                        <div key={l.id} className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 px-4 py-3 items-center" data-testid={`ligne-${l.id}`}>
                          <div className="flex-1 min-w-[120px] text-sm text-dd-night font-medium flex items-center gap-2">
                            <span className="break-words">{l.element_libelle}</span>
                            {l.appareil && (l.appareil.marque || l.appareil.modele || l.appareil.numero_serie) && (
                              <span className="text-xs text-dd-warm truncate max-w-[220px]" data-testid={`appareil-info-${l.id}`}>{[l.appareil.marque, l.appareil.modele].filter(Boolean).join(" ")}{l.appareil.numero_serie ? ` · S/N ${l.appareil.numero_serie}` : ""}</span>
                            )}
                            {l.numero_observation ? (() => {
                              const o = (etat.observations || []).find((x) => x.numero === l.numero_observation);
                              const isDef = !o || o.nature !== "usure_normale";
                              const incomplete = o && o.nature === "defaut" && !(o.description || "").trim();
                              return (
                                <button onClick={() => o && editObs(o)}
                                  className={`text-xs px-2 py-0.5 rounded-full font-semibold shrink-0 flex items-center gap-1 ${isDef ? "bg-destructive/10 text-destructive" : "bg-dd-blue/10 text-dd-blue"} ${incomplete ? "ring-1 ring-destructive" : ""}`}
                                  title={incomplete ? "Commentaire obligatoire manquant" : ""}
                                  data-testid={`obs-link-${l.numero_observation}`}>
                                  {o?.photos?.length ? <Camera size={11} /> : null}{isDef ? "obs." : "note"} {l.numero_observation}{incomplete ? " !" : ""}
                                </button>
                              );
                            })() : (!locked && (
                              <button onClick={() => addNoteObs(currentPiece.id, l.id)} title="Ajouter une remarque / photo (facultatif)"
                                className="h-8 w-8 flex items-center justify-center rounded-full text-dd-warm hover:text-dd-blue hover:bg-dd-light/60 shrink-0"
                                data-testid={`add-note-${l.id}`}><Camera size={15} /></button>
                            ))}
                          </div>
                          {showEntree && (
                            etat.source_entree_id ? (
                              <div className="w-[64px] shrink-0 flex justify-center" title="Code d'entrée (repris, lecture seule)">
                                <span data-testid={`entree-${l.id}-repris`} className={`inline-flex items-center justify-center min-w-[44px] h-9 px-2 rounded-md text-sm font-semibold ${l.code_entree === "D" ? "bg-destructive/15 text-destructive" : l.code_entree ? "bg-dd-blue/10 text-dd-blue" : "bg-dd-light/60 text-dd-warm"}`}>{l.code_entree || "—"}</span>
                              </div>
                            ) : (
                              <div className={colW}>
                                <CodeSegment value={l.code_entree} disabled={locked} testidPrefix={`entree-${l.id}`}
                                  onChange={(c) => setLigneCode(currentPiece.id, l.id, "code_entree", c)} />
                              </div>
                            )
                          )}
                          {showSortie && (
                            <div className={colW}>
                              <CodeSegment value={l.code_sortie} disabled={locked} testidPrefix={`sortie-${l.id}`}
                                onChange={(c) => setLigneCode(currentPiece.id, l.id, "code_sortie", c)} />
                            </div>
                          )}
                          {!locked && (
                            <div className="flex items-center gap-1 w-[128px] shrink-0 justify-end">
                              <button onClick={() => setAppareilEdit({ pieceId: currentPiece.id, ligneId: l.id, libelle: l.element_libelle, value: { marque: "", modele: "", numero_serie: "", photos: [], ...(l.appareil || {}) } })}
                                className={`h-11 w-10 flex items-center justify-center rounded-md border border-dd-light hover:bg-dd-light/60 ${l.appareil?.marque || l.appareil?.modele ? "text-dd-blue" : "text-dd-warm"}`}
                                title="Appareil : marque / modèle / n° de série (photo de l'étiquette)" data-testid={`appareil-ligne-${l.id}`}><Tag size={16} /></button>
                              <button onClick={() => setLigneEdit({ pieceId: currentPiece.id, ligneId: l.id, value: l.element_libelle })}
                                className="h-11 w-10 flex items-center justify-center text-dd-blue rounded-md border border-dd-light hover:bg-dd-light/60"
                                title="Modifier l'élément" data-testid={`edit-ligne-${l.id}`}><Pencil size={16} /></button>
                              <button onClick={() => setConfirm({ title: "Supprimer cet élément ?", message: `« ${l.element_libelle} » sera retiré de la pièce${l.numero_observation ? ` ainsi que l'observation n° ${l.numero_observation}` : ""}.`, onConfirm: () => removeLigne(currentPiece.id, l) })}
                                className="h-11 w-10 flex items-center justify-center text-destructive rounded-md border border-dd-light hover:bg-destructive/10"
                                title="Supprimer l'élément" data-testid={`delete-ligne-${l.id}`}><Trash2 size={16} /></button>
                            </div>
                          )}
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>

              {!locked && (
                <div className="px-4 py-3 border-t border-dd-light flex items-center justify-between gap-3">
                  <button onClick={() => setLigneEdit({ pieceId: currentPiece.id, ligneId: null, value: "" })}
                    className="h-11 px-3 text-sm text-dd-blue flex items-center gap-1.5 rounded-md border border-dd-blue/40 hover:bg-dd-light/60"
                    data-testid="add-ligne-btn">
                    <Plus size={15} /> Ajouter un élément
                  </button>
                  <button onClick={() => askRemovePiece(currentPiece)} className="h-11 px-3 text-sm text-destructive flex items-center gap-1.5"
                    data-testid="delete-piece-btn">
                    <Trash2 size={14} /> Supprimer cette pièce
                  </button>
                </div>
              )}
            </div>
          )}

          {step === pieces.length + 1 && (
            <SyntheseStep etat={etat} update={update} editObs={editObs} deleteObs={deleteObs} locked={locked}
              onSigned={(e) => setEtat(e)}
              onSent={(envois) => setEtat((p) => ({ ...p, envois: [...(p.envois || []), ...envois] }))} />
          )}

          {/* Nav prev/next */}
          <div className="flex items-center justify-between mt-5">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}
              data-testid="prev-btn" className="h-11 border-dd-light text-dd-night">
              <ArrowLeft size={16} className="mr-1" /> Précédent
            </Button>
            {step === 0 && !locked && (
              <Button variant="outline" onClick={() => setAddPieceOpen(true)} data-testid="add-piece-btn"
                className="h-11 border-dd-blue text-dd-blue">
                <Plus size={16} className="mr-1" /> Ajouter une pièce
              </Button>
            )}
            <Button disabled={step === steps.length - 1} onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              data-testid="next-btn" className="h-11 bg-dd-blue hover:bg-dd-night text-white">
              Suivant <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-dd-blue">Modifier la pièce</DialogTitle></DialogHeader>
          <Input value={renaming?.value || ""} data-testid="rename-input"
            onChange={(e) => setRenaming((r) => ({ ...r, value: e.target.value }))} className="h-11" />
          <DialogFooter>
            <Button className="bg-dd-blue text-white hover:bg-dd-night" data-testid="rename-save"
              onClick={() => { renamePiece(renaming.id, renaming.value); setRenaming(null); }}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="confirm-dialog">
          <DialogHeader><DialogTitle className="text-dd-blue">{confirm?.title}</DialogTitle></DialogHeader>
          <p className="text-sm text-dd-night">{confirm?.message}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-11 border-dd-light" onClick={() => setConfirm(null)} data-testid="confirm-cancel">Annuler</Button>
            <Button className="h-11 bg-destructive hover:bg-destructive/90 text-white" data-testid="confirm-delete"
              onClick={() => { confirm.onConfirm(); setConfirm(null); }}>Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appareil (étiquette) dialog */}
      <Dialog open={!!appareilEdit} onOpenChange={(o) => !o && setAppareilEdit(null)}>
        <DialogContent className="max-w-md" data-testid="appareil-dialog">
          <DialogHeader><DialogTitle className="text-dd-blue">Appareil — {appareilEdit?.libelle}</DialogTitle></DialogHeader>
          <p className="text-xs text-dd-warm -mt-2">Photographiez l'étiquette / plaque signalétique : l'IA pré-remplit marque, modèle et n° de série, à vérifier.</p>
          {appareilEdit && (
            <>
              <ItemPhotos etatId={etat.id} genre="etiquette" photos={appareilEdit.value.photos || []} testid="appareil-photos"
                onPhotos={(photos) => setAppareilEdit((a) => ({ ...a, value: { ...a.value, photos } }))}
                onLecture={(r) => setAppareilEdit((a) => ({ ...a, value: { ...a.value, marque: a.value.marque || r.marque || "", modele: a.value.modele || r.modele || "", numero_serie: a.value.numero_serie || r.numero_serie || "" } }))} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Marque" value={appareilEdit.value.marque} testid="appareil-marque" onChange={(v) => setAppareilEdit((a) => ({ ...a, value: { ...a.value, marque: v } }))} />
                <Field label="Modèle" value={appareilEdit.value.modele} testid="appareil-modele" onChange={(v) => setAppareilEdit((a) => ({ ...a, value: { ...a.value, modele: v } }))} />
                <Field label="N° de série" value={appareilEdit.value.numero_serie} testid="appareil-serie" full onChange={(v) => setAppareilEdit((a) => ({ ...a, value: { ...a.value, numero_serie: v } }))} />
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" className="h-11 border-dd-light" onClick={() => setAppareilEdit(null)}>Annuler</Button>
            <Button className="bg-dd-blue text-white hover:bg-dd-night h-11" data-testid="appareil-save" onClick={saveAppareil}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ligne (élément) dialog */}
      <Dialog open={!!ligneEdit} onOpenChange={(o) => !o && setLigneEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-dd-blue">{ligneEdit?.ligneId ? "Renommer l'élément" : "Ajouter un élément"}</DialogTitle></DialogHeader>
          <Input value={ligneEdit?.value || ""} data-testid="ligne-input" placeholder="Ex. Porte-serviettes — texte libre ou suggestion" autoFocus
            onKeyDown={(e) => e.key === "Enter" && saveLigne()}
            onChange={(e) => setLigneEdit((r) => ({ ...r, value: e.target.value }))} className="h-11" />
          {!ligneEdit?.ligneId && (() => {
            const q = (ligneEdit?.value || "").trim().toLowerCase();
            const existing = new Set((currentPiece?.lignes || []).map((l) => l.element_libelle.toLowerCase()));
            const list = suggestions.filter((s) => !existing.has(s.libelle.toLowerCase()) && (!q || s.libelle.toLowerCase().includes(q))).slice(0, 12);
            return list.length ? (
              <div className="max-h-56 overflow-y-auto -mx-1 divide-y divide-dd-light border border-dd-light rounded-md" data-testid="ligne-suggestions">
                {list.map((s) => (
                  <button key={s.libelle} onClick={() => setLigneEdit((r) => ({ ...r, value: s.libelle }))} data-testid="ligne-suggestion"
                    className="w-full text-left px-3 h-11 text-sm text-dd-night hover:bg-dd-light/60 flex items-center justify-between gap-2">
                    <span>{s.libelle}</span>
                    <span className="text-[10px] uppercase tracking-wide text-dd-warm">{s.source === "catalogue" ? "catalogue" : s.source === "utilise" ? "déjà utilisé" : "autre pièce"}</span>
                  </button>
                ))}
              </div>
            ) : q ? <p className="text-xs text-dd-warm">Aucune suggestion — « {ligneEdit.value} » sera ajouté tel quel.</p> : null;
          })()}
          <DialogFooter>
            <Button className="bg-dd-blue text-white hover:bg-dd-night h-11" data-testid="ligne-save" onClick={saveLigne}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add piece dialog */}
      <Dialog open={addPieceOpen} onOpenChange={setAddPieceOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-dd-blue">Ajouter une pièce</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            {PIECE_TYPES.map(([code, label]) => (
              <button key={code} data-testid={`add-piece-${code}`} onClick={() => addPiece(code, label)}
                className="h-11 rounded-md border border-dd-light text-sm text-dd-night hover:bg-dd-light/50">
                {label}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ObservationDialog
        open={obsDialog.open}
        onOpenChange={(o) => setObsDialog((d) => ({ ...d, open: o }))}
        observation={obsDialog.obs}
        onChange={(obs) => setObsDialog((d) => ({ ...d, obs }))}
        onSave={locked ? () => setObsDialog({ open: false, obs: null }) : saveObs}
        etatId={etat.id}
        locked={locked}
        showVetuste={etat.type_constat !== "entree"}
        anneeConstat={parseInt((etat.date_constat || "").slice(0, 4), 10) || new Date().getFullYear()}
      />

      {/* Réouverture (admin) */}
      <Dialog open={reopen !== null} onOpenChange={(o) => !o && setReopen(null)}>
        <DialogContent className="max-w-md" data-testid="reopen-dialog">
          <DialogHeader><DialogTitle className="text-dd-blue">Réouvrir le constat signé</DialogTitle></DialogHeader>
          <p className="text-sm text-dd-night">Les signatures actuelles et le PDF archivé seront conservés dans le journal de réouverture. Le constat repassera « en cours » et devra être signé à nouveau.</p>
          <Label className="text-dd-night">Motif (obligatoire)</Label>
          <Textarea rows={3} value={reopen || ""} onChange={(e) => setReopen(e.target.value)} data-testid="reopen-motif" />
          <DialogFooter>
            <Button onClick={doReopen} disabled={!(reopen || "").trim()} className="h-11 bg-dd-blue text-white hover:bg-dd-night" data-testid="reopen-confirm">Réouvrir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepBtn({ active, onClick, icon: Icon, label, testid, done }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm text-left transition-colors ${
        active ? "bg-dd-blue text-white" : "text-dd-night hover:bg-dd-light/60"
      }`}>
      <Icon size={16} className="shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {done && !active && <Check size={14} className="text-dd-blue" />}
    </button>
  );
}

function StatutEnvoi({ e }) {
  const S = { envoye: ["Envoyé", "bg-dd-light text-dd-night"], retarde: ["Retardé", "bg-amber-100 text-amber-900"], livre: ["Livré", "bg-dd-blue/10 text-dd-blue"],
    ouvert: ["Ouvert", "bg-emerald-100 text-emerald-800"], rebond: ["Rebond — adresse invalide", "bg-destructive/10 text-destructive"],
    spam: ["Marqué spam", "bg-destructive/10 text-destructive"], echec: ["Échec", "bg-destructive/10 text-destructive"] };
  const [label, cls] = S[e.statut] || ["Envoyé", "bg-dd-light text-dd-night"];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid="envoi-statut" title={e.statut_date ? new Date(e.statut_date).toLocaleString("fr-CH") : ""}>
      {label}{e.statut_date ? ` · ${new Date(e.statut_date).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" })}` : ""}
    </span>
  );
}

function Field({ label, value, onChange, disabled, type = "text", full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-dd-night text-sm">{label}</Label>
      <Input type={type} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="mt-1 h-11" data-testid={testid} />
    </div>
  );
}

function Colocataires({ side, etat, update, locked }) {
  const key = `colocataires_${side}s`;
  const list = etat[key] || [];
  const setList = (fn) => update((p) => ({ ...p, [key]: fn(p[key] || []) }));
  return (
    <div className="sm:col-span-2" data-testid={`colocataires-${side}`}>
      {list.map((c, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pl-3 border-l-2 border-dd-light">
          <Field label={`Locataire ${side} ${i + 2}`} value={c.nom} disabled={locked} testid={`coloc-${side}-nom-${i}`}
            onChange={(v) => setList((l) => l.map((x, j) => (j === i ? { ...x, nom: v } : x)))} />
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Field label={`E-mail (facultatif)`} type="email" value={c.email} disabled={locked} testid={`coloc-${side}-email-${i}`}
                onChange={(v) => setList((l) => l.map((x, j) => (j === i ? { ...x, email: v } : x)))} />
            </div>
            {!locked && <button onClick={() => setList((l) => l.filter((_, j) => j !== i))} className="h-11 w-11 inline-flex items-center justify-center text-destructive" data-testid={`coloc-${side}-remove-${i}`}><Trash2 size={16} /></button>}
          </div>
        </div>
      ))}
      {!locked && (
        <button onClick={() => setList((l) => [...l, { nom: "", email: "" }])} data-testid={`coloc-${side}-add`}
          className="mt-2 text-sm text-dd-blue flex items-center gap-1.5 h-9"><Plus size={15} /> Ajouter un locataire {side} (colocation)</button>
      )}
    </div>
  );
}

function InfosStep({ etat, setField, update, locked, addCle, addCompteur, marquerRelu }) {
  return (
    <div className="space-y-6">
      <Section title="Objet loué & parties">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-dd-night text-sm">N° EGID</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="detail-egid" inputMode="numeric" value={etat.egid || ""} disabled={locked} onChange={(e) => setField("egid", e.target.value)} className="h-11" />
              <RegblButton egid={etat.egid} disabled={locked}
                onResult={(r) => update((p) => ({ ...p, immeuble: r.immeuble || p.immeuble, npa: r.npa || p.npa, localite: r.localite || p.localite }))} />
            </div>
          </div>
          <Field label="N° de dossier" value={etat.numero_dossier} disabled={locked} onChange={(v) => setField("numero_dossier", v)} />
          <Field label="Immeuble" value={etat.immeuble} disabled={locked} onChange={(v) => setField("immeuble", v)} />
          <Field label="NPA" value={etat.npa} disabled={locked} onChange={(v) => setField("npa", v)} />
          <Field label="Localité" value={etat.localite} disabled={locked} onChange={(v) => setField("localite", v)} />
          <Field label="Étage" value={etat.etage} disabled={locked} onChange={(v) => setField("etage", v)} />
          <Field label="N° d'objet" value={etat.numero_objet} disabled={locked} onChange={(v) => setField("numero_objet", v)} />
          <Field label="Nombre de pièces" value={etat.nombre_pieces} disabled={locked} onChange={(v) => setField("nombre_pieces", v)} />
          <Field label="Surface (m²)" value={etat.surface} disabled={locked} onChange={(v) => setField("surface", v)} />
          <Field label="Propriétaire" value={etat.proprietaire} disabled={locked} onChange={(v) => setField("proprietaire", v)} />
          <Field label="E-mail du propriétaire" type="email" value={etat.email_proprietaire} disabled={locked} onChange={(v) => setField("email_proprietaire", v)} />
          <Field label="Dépendances" value={etat.dependances} disabled={locked} onChange={(v) => setField("dependances", v)} full />
          <Field label="Locataire entrant" value={etat.locataire_entrant} disabled={locked} onChange={(v) => setField("locataire_entrant", v)} />
          <Field label="E-mail du locataire entrant" type="email" value={etat.email_locataire_entrant} disabled={locked} onChange={(v) => setField("email_locataire_entrant", v)} />
          {etat.type_constat !== "sortie" && <Colocataires side="entrant" etat={etat} update={update} locked={locked} />}
          <Field label="Locataire sortant" value={etat.locataire_sortant} disabled={locked} onChange={(v) => setField("locataire_sortant", v)} />
          <Field label="E-mail du locataire sortant" type="email" value={etat.email_locataire_sortant} disabled={locked} onChange={(v) => setField("email_locataire_sortant", v)} />
          {etat.type_constat !== "entree" && <Colocataires side="sortant" etat={etat} update={update} locked={locked} />}
          <Field label="Nouvelle adresse du sortant" value={etat.nouvelle_adresse_sortant} disabled={locked} onChange={(v) => setField("nouvelle_adresse_sortant", v)} full />
          <Field label="Téléphone" value={etat.telephone} disabled={locked} onChange={(v) => setField("telephone", v)} />
          <Field label="Gérant responsable" value={etat.gerant_responsable} disabled={locked} onChange={(v) => setField("gerant_responsable", v)} />
          <Field label="Date du constat" type="date" value={etat.date_constat} disabled={locked} onChange={(v) => setField("date_constat", v)} />
          <Field label="Heure du constat" type="time" value={etat.heure_constat} disabled={locked} onChange={(v) => setField("heure_constat", v)} />
        </div>
      </Section>

      {etat.import_source && (
        <Section title="Documents importés" action={!etat.import_source.relu_le && (
          <Button size="sm" onClick={marquerRelu} data-testid="import-relu-btn" className="h-9 bg-dd-blue hover:bg-dd-night text-white">
            <CheckCircle2 size={14} className="mr-1" /> Relu et validé
          </Button>
        )}>
          {etat.import_source.relu_le
            ? <p className="text-xs text-green-700 mb-2" data-testid="import-relu-info">Relu et validé le {new Date(etat.import_source.relu_le).toLocaleDateString("fr-CH")}{etat.import_source.relu_par ? ` par ${etat.import_source.relu_par}` : ""}.</p>
            : <p className="text-xs text-dd-warm mb-2">Constat recréé depuis un ancien document (confiance de lecture {Math.round((etat.import_source.confiance || 0) * 100)}%). Relisez et corrigez avant signature.</p>}
          <ul className="space-y-1" data-testid="import-source-files">
            {(etat.import_source.fichiers || []).map((f) => (
              <li key={f.id}><a href={`${API}${f.url.replace(/^\/api/, "")}`} target="_blank" rel="noreferrer" className="text-sm text-dd-blue underline">{f.name}</a></li>
            ))}
          </ul>
          {etat.import_source.avertissements?.length > 0 && (
            <ul className="list-disc ml-5 mt-3 text-xs text-amber-800 space-y-0.5">{etat.import_source.avertissements.map((a, i) => <li key={i}>{a}</li>)}</ul>
          )}
        </Section>
      )}

      <Section title="Conditions préalables à la remise des clés">
        <div className="space-y-3">
          {(etat.conditions_prealables || []).map((c, i) => (
            <label key={i} className="flex items-start gap-3 text-sm text-dd-night cursor-pointer">
              <Checkbox checked={c.checked} disabled={locked} data-testid={`condition-${i}`}
                onCheckedChange={(v) => update((p) => ({
                  ...p, conditions_prealables: p.conditions_prealables.map((x, j) => j === i ? { ...x, checked: !!v } : x),
                }))} />
              {c.libelle}
            </label>
          ))}
        </div>
      </Section>

      <Section title="Clés & badges" action={!locked && <AddBtn onClick={addCle} label="Ajouter une clé / badge" testid="add-cle" />}>
        {(() => {
          const isEntree = etat.type_constat === "entree";
          const isSortie = etat.type_constat === "sortie";
          const remisLocked = (c) => locked || (isSortie && c.origine === "entree");
          const setCle = (i, k, v) => update((p) => ({ ...p, cles: p.cles.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
          return (
            <>
              <p className="text-xs text-dd-warm mb-3">
                {isEntree && "État des lieux d'entrée : indiquez la date de remise. La restitution sera saisie à la sortie."}
                {isSortie && (etat.source_entree_id ? "Clés reprises du constat d'entrée : saisissez la date de restitution." : "Indiquez la remise (passé) et la date de restitution.")}
                {!isEntree && !isSortie && "Indiquez la date de remise et, le cas échéant, la date de restitution."}
              </p>
              {(etat.cles || []).length === 0 && <p className="text-sm text-dd-warm">Aucune clé enregistrée.</p>}
              {(etat.cles || []).length > 0 && (
                <div className="hidden sm:grid grid-cols-[minmax(160px,1fr)_130px_64px_150px_150px_minmax(60px,auto)_40px] gap-2 px-1 mb-1 text-xs uppercase tracking-wide text-dd-warm">
                  <div>Désignation</div><div>N° clé / badge</div><div>Nb</div><div>Remis le</div><div>{isEntree ? "" : "Restitué le"}</div><div>Photos</div><div />
                </div>
              )}
              <div className="space-y-2">
                {(etat.cles || []).map((c, i) => (
                  <div key={i} className="grid grid-cols-2 sm:grid-cols-[minmax(160px,1fr)_130px_64px_150px_150px_minmax(60px,auto)_40px] gap-2 items-center" data-testid={`cle-row-${i}`}>
                    <Input placeholder="Ex. Clé de la cave" value={c.designation || ""} disabled={remisLocked(c)} data-testid={`cle-designation-${i}`}
                      onChange={(e) => setCle(i, "designation", e.target.value)} className="h-11" />
                    <Input placeholder="R21093" value={c.numero || ""} disabled={remisLocked(c)} data-testid={`cle-numero-${i}`}
                      onChange={(e) => setCle(i, "numero", e.target.value)} className="h-11" />
                    <Input type="number" inputMode="numeric" min="1" placeholder="1" value={c.nombre ?? ""} disabled={remisLocked(c)} data-testid={`cle-nombre-${i}`}
                      onChange={(e) => setCle(i, "nombre", e.target.value)} className="h-11" />
                    <Input type="date" value={c.date_remise || ""} disabled={remisLocked(c)} data-testid={`cle-remise-${i}`}
                      onChange={(e) => setCle(i, "date_remise", e.target.value)} className="h-11" />
                    {isEntree ? <div className="text-xs text-dd-warm px-1">— à la sortie</div> : (
                      <Input type="date" value={c.date_restitution || ""} disabled={locked} data-testid={`cle-restitution-${i}`}
                        onChange={(e) => setCle(i, "date_restitution", e.target.value)} className="h-11" />
                    )}
                    <ItemPhotos etatId={etat.id} genre="cle" photos={c.photos || []} disabled={locked} testid={`cle-photos-${i}`}
                      onPhotos={(photos) => setCle(i, "photos", photos)}
                      onLecture={(r) => update((p) => ({ ...p, cles: p.cles.map((x, j) => j === i ? { ...x, designation: x.designation || r.designation || "", numero: x.numero || r.numero || "", nombre: x.nombre || r.nombre || x.nombre } : x) }))} />
                    {!locked && <button onClick={() => update((p) => ({ ...p, cles: p.cles.filter((_, j) => j !== i) }))} className="text-destructive h-11 w-10 flex items-center justify-center" data-testid={`cle-delete-${i}`}><Trash2 size={16} /></button>}
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </Section>

      <Section title="Relevés de compteurs" action={!locked && <AddBtn onClick={addCompteur} label="Ajouter un compteur" testid="add-compteur" />}>
        {(() => {
          const isEntree = etat.type_constat === "entree";
          const isSortie = etat.type_constat === "sortie";
          const setC = (i, k, v) => update((p) => ({ ...p, compteurs: p.compteurs.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
          return (
            <>
              <p className="text-xs text-dd-warm mb-3">
                {isEntree && "Relevé à l'entrée. L'index de sortie sera saisi lors de l'état des lieux de sortie."}
                {isSortie && etat.source_entree_id && "Compteurs repris du constat d'entrée (lecture seule) : saisissez uniquement l'index de sortie."}
                {isSortie && !etat.source_entree_id && "Indiquez l'index d'entrée (si connu) et l'index de sortie."}
                {!isEntree && !isSortie && "Indiquez l'index à l'entrée et à la sortie."}
              </p>
              {(etat.compteurs || []).length === 0 && <p className="text-sm text-dd-warm">Aucun compteur enregistré.</p>}
              {(etat.compteurs || []).length > 0 && (
                <div className="hidden sm:grid grid-cols-[1fr_150px_140px_140px_minmax(60px,auto)_40px] gap-2 px-1 mb-1 text-xs uppercase tracking-wide text-dd-warm">
                  <div>Type</div><div>Numéro</div><div>Index entrée</div><div>{isEntree ? "" : "Index sortie"}</div><div>Photos</div><div />
                </div>
              )}
              <div className="space-y-2">
                {(etat.compteurs || []).map((c, i) => {
                  const ro = locked || (isSortie && c.origine === "entree");
                  return (
                    <div key={i} className="grid grid-cols-2 sm:grid-cols-[1fr_150px_140px_140px_minmax(60px,auto)_40px] gap-2 items-center" data-testid={`compteur-row-${i}`}>
                      <Input placeholder="Électricité, eau chaude…" value={c.type || ""} disabled={ro} data-testid={`compteur-type-${i}`}
                        onChange={(e) => setC(i, "type", e.target.value)} className="h-11" />
                      <Input placeholder="N° compteur" value={c.numero || ""} disabled={ro} data-testid={`compteur-numero-${i}`}
                        onChange={(e) => setC(i, "numero", e.target.value)} className="h-11" />
                      <Input placeholder="Index entrée" inputMode="decimal" value={c.index_entree ?? c.index ?? ""} disabled={ro} data-testid={`compteur-entree-${i}`}
                        onChange={(e) => setC(i, "index_entree", e.target.value)} className="h-11" />
                      {isEntree ? <div className="text-xs text-dd-warm px-1">— à la sortie</div> : (
                        <Input placeholder="Index sortie" inputMode="decimal" value={c.index_sortie ?? ""} disabled={locked} data-testid={`compteur-sortie-${i}`}
                          onChange={(e) => setC(i, "index_sortie", e.target.value)} className="h-11" />
                      )}
                      <ItemPhotos etatId={etat.id} genre="compteur" photos={c.photos || []} disabled={locked} testid={`compteur-photos-${i}`}
                        onPhotos={(photos) => setC(i, "photos", photos)}
                        onLecture={(r) => update((p) => ({ ...p, compteurs: p.compteurs.map((x, j) => {
                          if (j !== i) return x;
                          const idxKey = (isEntree || (!isSortie && !x.index_entree)) ? "index_entree" : "index_sortie";
                          return { ...x, type: x.type || r.type || "", numero: x.numero || r.numero || "", [idxKey]: x[idxKey] || r.index || "" };
                        }) }))} />
                      {!locked && <button onClick={() => update((p) => ({ ...p, compteurs: p.compteurs.filter((_, j) => j !== i) }))} className="text-destructive h-11 w-10 flex items-center justify-center" data-testid={`compteur-delete-${i}`}><Trash2 size={16} /></button>}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </Section>

      <Section title="Remarques générales">
        <Textarea value={etat.remarques_generales || ""} disabled={locked} rows={4}
          onChange={(e) => setField("remarques_generales", e.target.value)} data-testid="remarques-generales" />
      </Section>
    </div>
  );
}

function SyntheseStep({ etat, update, editObs, deleteObs, locked, onSent, onSigned }) {
  const obs = etat.observations || [];
  const envois = etat.envois || [];
  const obsEntree = etat.observations_entree || [];
  const totalLoc = obs.reduce((s, o) => s + (o.part_locataire || 0), 0);
  const totalBail = obs.reduce((s, o) => s + (o.part_bailleur || 0), 0);
  const [sendingAvis, setSendingAvis] = useState(false);
  const onRenvoyer = async (e) => {
    if (!window.confirm(`Renvoyer le procès-verbal à ${e.email} ?`)) return;
    try {
      const { data } = await api.post(`/etats/${etat.id}/envois/renvoyer`, { destinataire: e.destinataire, email: e.email });
      onSent(data.envois);
      toast.success(`PV renvoyé à ${e.email}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const sendAvis = async () => {
    const emailsSortants = locataires(etat, "sortant").map((l) => l.email).filter((e) => e.includes("@"));
    if (!emailsSortants.length) return toast.error("Saisissez l'e-mail du locataire sortant dans « Informations générales »");
    if (!window.confirm(`Envoyer l'avis des défauts (avec le procès-verbal) à ${emailsSortants.join(", ")} ?`)) return;
    setSendingAvis(true);
    try {
      const { data } = await api.post(`/etats/${etat.id}/send-avis`, { joindre_pv: true, copie_gerant: true });
      const ok = data.envois.filter((e) => e.ok).length;
      ok ? toast.success(`Avis envoyé à ${ok} destinataire${ok > 1 ? "s" : ""}`) : toast.error("Échec de l'envoi");
      onSigned(data.etat);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSendingAvis(false); }
  };
  const downloadAvis = async () => {
    try {
      const r = await api.get(`/etats/${etat.id}/avis-defauts.pdf`, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(r.headers["content-disposition"] || "")?.[1] || "avis-des-defauts.pdf";
      const u = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: u, download: name }).click();
      setTimeout(() => URL.revokeObjectURL(u), 5000);
      toast.success("Avis des défauts généré");
    } catch (e) { toast.error("Impossible de générer l'avis des défauts"); }
  };
  return (
    <div className="space-y-6">
      <SignaturesSection etat={etat} locked={locked} onSigned={onSigned} />

      {(etat.journal || []).length > 0 && (
        <Section title="Journal de réouverture">
          {etat.journal.map((j, i) => (
            <div key={i} className="text-sm text-dd-night border-l-2 border-dd-warm pl-3 mb-2" data-testid={`journal-${i}`}>
              <span className="text-dd-warm">{new Date(j.date).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })} · {j.par}</span> — {j.motif}
            </div>
          ))}
        </Section>
      )}

      <Section title="Procès-verbal PDF">
        <p className="text-sm text-dd-warm mb-3">Document A4 généré sur le serveur avec l'identité de la gérance. Les adresses e-mail des parties se saisissent dans « Informations générales ».</p>
        <div className="flex flex-wrap items-center gap-2">
          <PdfActions etat={etat} onSent={onSent} />
          {etat.type_constat !== "entree" && obs.some((o) => o.imputation === "locataire") && (
            <>
              <Button variant="outline" onClick={downloadAvis} className="h-11 border-dd-blue text-dd-blue" data-testid="avis-defauts-btn">
                <FileWarning size={16} className="mr-1" /> Avis des défauts (lettre recommandée)
              </Button>
              <Button variant="outline" onClick={sendAvis} disabled={sendingAvis} className="h-11 border-dd-blue text-dd-blue" data-testid="avis-send-btn">
                <Mail size={16} className="mr-1" /> {sendingAvis ? "Envoi…" : "Envoyer l'avis par e-mail"}
              </Button>
            </>
          )}
        </div>
        {(etat.avis_defauts_genere_le || etat.avis_envoye_le) && (
          <p className="text-xs text-dd-warm mt-2" data-testid="avis-status">
            {etat.avis_defauts_genere_le && <>Avis généré le {new Date(etat.avis_defauts_genere_le).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}. </>}
            {etat.avis_envoye_le ? <span className="text-dd-blue font-medium">Envoyé par e-mail au locataire le {new Date(etat.avis_envoye_le).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}.</span> : <span className="text-destructive">Pas encore envoyé par e-mail.</span>}
          </p>
        )}
        {envois.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="envois-list">
            <div className="text-xs uppercase tracking-wider text-dd-warm">Historique des envois</div>
            {[...envois].reverse().slice(0, 8).map((e, i) => (
              <div key={i} className="text-sm text-dd-night flex items-center gap-2 flex-wrap" data-testid={`envoi-row-${i}`}>
                <span className={`w-2 h-2 rounded-full ${!e.ok || ["rebond", "spam", "echec"].includes(e.statut) ? "bg-destructive" : e.statut === "ouvert" ? "bg-emerald-500" : "bg-dd-blue"}`} />
                {new Date(e.date).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })} · {e.email}
                {!e.ok ? <span className="text-destructive">— échec</span> : <StatutEnvoi e={e} />}
                {e.ok && e.destinataire?.startsWith("locataire") && ["envoye", "livre", "retarde", undefined, null].includes(e.statut) && (Date.now() - new Date(e.date).getTime()) > 7 * 86400000 && (
                  <button onClick={() => onRenvoyer(e)} className="text-xs text-dd-blue underline flex items-center gap-1" data-testid={`renvoyer-envoi-${i}`}><RefreshCw size={12} /> Renvoyer</button>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Observations numérotées">
        {obs.length === 0 && <p className="text-sm text-dd-warm">Aucune observation. Les défauts (code D) créent une observation.</p>}
        <div className="space-y-2">
          {[...obs].sort((a, b) => a.numero - b.numero).map((o) => (
            <div key={o.numero} className="flex items-start gap-3 p-3 rounded-md border border-dd-light" data-testid={`obs-item-${o.numero}`}>
              <div className="w-8 h-8 rounded-full bg-dd-blue text-white flex items-center justify-center text-sm font-semibold shrink-0">{o.numero}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-dd-night">{o.piece} — {o.element}</div>
                <div className="text-sm text-dd-warm">{o.description || (o.nature === "defaut" ? <em className="text-destructive">Commentaire obligatoire manquant</em> : <em>Sans commentaire</em>)}</div>
                {o.imputation && <Badge className="mt-1 bg-dd-light text-dd-night border-0">{IMPUT_LABEL[o.imputation]}</Badge>}
                {o.part_locataire != null && (
                  <div className="text-xs text-dd-night mt-1" data-testid={`obs-vetuste-${o.numero}`}>
                    CHF {Number(o.montant).toFixed(2)} · part locataire <strong>CHF {o.part_locataire.toFixed(2)}</strong> · bailleur CHF {o.part_bailleur.toFixed(2)}
                  </div>
                )}
                {(o.photos || []).length > 0 && (
                  <div className="flex gap-1.5 mt-2">
                    {o.photos.map((ph) => <img key={ph.id} src={photoSrc(ph)} alt="" className={`h-14 w-14 object-cover rounded border ${ph.local ? "border-dashed border-dd-warm" : "border-dd-light"}`} />)}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => editObs(o)} className="text-dd-blue h-11 w-10 flex items-center justify-center" data-testid={`edit-obs-${o.numero}`}><Pencil size={16} /></button>
                {!locked && <button onClick={() => deleteObs(o.numero)} className="text-destructive h-11 w-10 flex items-center justify-center" data-testid={`delete-obs-${o.numero}`}><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
        </div>
        {(totalLoc > 0 || totalBail > 0) && (
          <div className="mt-4 rounded-md bg-dd-light/40 p-3 text-sm text-dd-night" data-testid="vetuste-total">
            Total à charge du locataire : <strong className="text-dd-blue">CHF {totalLoc.toFixed(2)}</strong> · Part bailleur (vétusté) : CHF {totalBail.toFixed(2)}
          </div>
        )}
      </Section>

      {obsEntree.length > 0 && (
        <Section title="Observations du constat d'entrée (référence)">
          <div className="space-y-1">
            {obsEntree.map((o) => (
              <div key={o.numero} className="text-sm text-dd-night" data-testid={`obs-entree-${o.numero}`}>
                <span className="text-dd-warm">n° {o.numero}</span> {o.piece} — {o.element}{o.description ? ` : ${o.description}` : ""}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Suites données au constat">
        <label className="flex items-start gap-3 text-sm text-dd-night cursor-pointer">
          <Checkbox checked={etat.suites?.avis_defauts_recommande} disabled={locked} data-testid="avis-recommande"
            onCheckedChange={(v) => update((p) => ({ ...p, suites: { ...p.suites, avis_defauts_recommande: !!v } }))} />
          <span>
            <strong>Avis des défauts à notifier par pli recommandé.</strong>
            <span className="block text-dd-warm mt-1">
              À notifier dans les 2 à 3 jours ouvrables suivant la restitution si le PV signé ne liste
              pas les défauts de manière reconnaissable (CO 267a). Ne jamais notifier par simple e-mail.
            </span>
          </span>
        </label>
        <Textarea className="mt-3" rows={2} placeholder="Notes complémentaires (facultatif)…" value={etat.suites?.texte || ""} disabled={locked}
          onChange={(e) => update((p) => ({ ...p, suites: { ...p.suites, texte: e.target.value } }))} />
      </Section>

      <Section title="Actions à entreprendre" action={!locked && <AddBtn testid="add-action" label="Ajouter une action"
        onClick={() => update((p) => ({ ...p, suites: { ...p.suites, actions: [...(p.suites?.actions || []), { id: crypto.randomUUID(), texte: "", responsable: "bailleur", fait: false, sur_pv: true }] } }))} />}>
        {(() => {
          const actions = etat.suites?.actions || [];
          const setA = (id, patch) => update((p) => ({ ...p, suites: { ...p.suites, actions: (p.suites?.actions || []).map((a) => a.id === id ? { ...a, ...patch } : a) } }));
          const RESP = [["bailleur", "Bailleur"], ["locataire", "Locataire"], ["gerance", "Gérance"]];
          const done = actions.filter((a) => a.fait).length;
          return (
            <>
              <p className="text-xs text-dd-warm mb-3">Votre liste de suivi pour finaliser devis et travaux. Seules les actions cochées « PV » apparaissent sur le procès-verbal.
                {actions.length > 0 && <span className="ml-2 font-medium text-dd-blue" data-testid="actions-progress">{done}/{actions.length} faites</span>}</p>
              {actions.length === 0 && <p className="text-sm text-dd-warm">Aucune action.</p>}
              {actions.length > 0 && (
                <div className="hidden sm:grid grid-cols-[40px_1fr_150px_64px_40px] gap-2 px-1 mb-1 text-xs uppercase tracking-wide text-dd-warm">
                  <div>Fait</div><div>Action</div><div>Responsable</div><div>PV</div><div />
                </div>
              )}
              <div className="space-y-2">
                {actions.map((a, i) => (
                  <div key={a.id} className={`grid grid-cols-2 sm:grid-cols-[40px_1fr_150px_64px_40px] gap-2 items-center ${a.fait ? "opacity-60" : ""}`} data-testid={`action-row-${i}`}>
                    <div className="flex justify-center"><Checkbox checked={!!a.fait} disabled={locked} data-testid={`action-fait-${i}`} onCheckedChange={(v) => setA(a.id, { fait: !!v })} /></div>
                    <Input value={a.texte} disabled={locked} placeholder="Ex. Devis remplacement poêle à pellets" data-testid={`action-texte-${i}`}
                      onChange={(e) => setA(a.id, { texte: e.target.value })} className={`h-11 ${a.fait ? "line-through" : ""}`} />
                    <Select value={a.responsable} disabled={locked} onValueChange={(v) => setA(a.id, { responsable: v })}>
                      <SelectTrigger className="h-11" data-testid={`action-resp-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{RESP.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex justify-center"><Checkbox checked={!!a.sur_pv} disabled={locked} title="Visible sur le PV" data-testid={`action-pv-${i}`} onCheckedChange={(v) => setA(a.id, { sur_pv: !!v })} /></div>
                    {!locked && <button onClick={() => update((p) => ({ ...p, suites: { ...p.suites, actions: p.suites.actions.filter((x) => x.id !== a.id) } }))} className="text-destructive h-11 w-10 flex items-center justify-center" data-testid={`action-delete-${i}`}><Trash2 size={16} /></button>}
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </Section>

      <div className="bg-dd-light/40 border border-dd-light rounded-lg p-4 text-xs text-dd-night leading-relaxed">
        <p className="font-semibold mb-1">À savoir</p>
        <p>La signature au doigt sur tablette est une signature électronique <strong>simple</strong>, non qualifiée au sens de la SCSE.
          L'état des lieux n'est soumis à aucune forme légale : c'est un moyen de preuve librement apprécié par le juge (CPC 157).</p>
        <p className="mt-2">L'état des lieux n'est pas légalement obligatoire. Le contrat-cadre romand n'a plus force obligatoire générale
          depuis le 1er juillet 2020 et ne s'applique que si les parties en ont convenu.</p>
      </div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="bg-white rounded-lg border border-dd-light p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-dd-blue">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function AddBtn({ onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="text-sm text-dd-blue flex items-center gap-1.5">
      <Plus size={15} /> {label}
    </button>
  );
}
